import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Table,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Alert,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Tooltip
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';
import {
  selectAllSongs,
  selectSongsLoading,
  selectSongsError,
} from '../../slices/song/list/reducer';
import {
  selectAllLibraries,
  selectLibrariesLoading,
  selectLibrariesError,
  selectLibrariesSuccess,
  selectLibraryImporting,
  clearError,
  clearSuccess
} from '../../slices/songLibrary/reducer';
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { fetchSongs, updateSong, deleteSong } from "../../slices/song/list/thunk";
import {
  fetchSongLibraries,
  addSongLibrary,
  editSongLibrary,
  removeSongLibrary,
  importSongLibraryFromCsv
} from "../../slices/songLibrary/thunk";
import { addLibrarySong, addBulkLibrarySongs } from "../../slices/songLibrarySong/thunk";
import { authUserManager } from "../../helpers/api_helper";

const SongLibraries = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Songs selectors
  const songs = useSelector(selectAllSongs);
  const loading = useSelector(selectSongsLoading);
  const error = useSelector(selectSongsError);

  // Libraries selectors
  const libraries = useSelector(selectAllLibraries);
  const librariesLoading = useSelector(selectLibrariesLoading);
  const librariesError = useSelector(selectLibrariesError);
  const librariesSuccess = useSelector(selectLibrariesSuccess);
  const importing = useSelector(selectLibraryImporting);

  const eventId = authUserManager.getAdminEventId();

  // Check URL params for active tab
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam === 'my-libraries' ? '2' : '1');
  const [selectedRows, setSelectedRows] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');

  // Library management state
  const [modal, setModal] = useState(false);
  const [editingLibrary, setEditingLibrary] = useState(null);
  const [libraryName, setLibraryName] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);
  const [libraryToDelete, setLibraryToDelete] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // Song edit/delete state
  const [editSongModal, setEditSongModal] = useState(false);
  const [deleteSongModal, setDeleteSongModal] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [songToDelete, setSongToDelete] = useState(null);
  const [songTitle, setSongTitle] = useState('');
  const [songArtist, setSongArtist] = useState('');

  // Add to library summary state
  const [addSummary, setAddSummary] = useState(null);

  // CSV import state
  const [importModal, setImportModal] = useState(false);
  const [importLibraryId, setImportLibraryId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importError, setImportError] = useState(null);
  const [importSummary, setImportSummary] = useState(null);

  const isAdmin = authUserManager.isAdmin();

  useEffect(() => {
    dispatch(fetchSongs());
    dispatch(fetchSongLibraries());
  }, [dispatch]);

  // Update active tab when URL param changes
  useEffect(() => {
    if (tabParam === 'my-libraries') {
      setActiveTab('2');
    }
  }, [tabParam]);

  useEffect(() => {
    if (librariesSuccess) {
      setTimeout(() => dispatch(clearSuccess()), 3000);
    }
  }, [librariesSuccess, dispatch]);

  useEffect(() => {
    if (librariesError) {
      setTimeout(() => dispatch(clearError()), 5000);
    }
  }, [librariesError, dispatch]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const handleRowSelect = (rowId) => {
    setSelectedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const handleSelectAll = (checked) => {
    const newSelectedRows = {};
    if (checked) {
      // Only select rows visible on the current page
      table.getRowModel().rows.forEach(row => {
        newSelectedRows[row.original.id] = true;
      });
    }
    setSelectedRows(newSelectedRows);
  };

  // Library management handlers
  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setEditingLibrary(null);
      setLibraryName('');
    }
  };

  const handleAddLibrary = () => {
    setEditingLibrary(null);
    setLibraryName('');
    toggleModal();
  };

  const handleEditLibrary = (library) => {
    setEditingLibrary(library);
    setLibraryName(library.name);
    toggleModal();
  };

  const handleSubmitLibrary = (e) => {
    e.preventDefault();
    if (editingLibrary) {
      dispatch(editSongLibrary({ id: editingLibrary.id, data: { name: libraryName } }));
    } else {
      dispatch(addSongLibrary({ name: libraryName }));
    }
    toggleModal();
  };

  const handleDeleteLibrary = (library) => {
    setLibraryToDelete(library);
    setDeleteModal(true);
  };

  const confirmDelete = () => {
    if (libraryToDelete) {
      dispatch(removeSongLibrary(libraryToDelete.id));
      setDeleteModal(false);
      setLibraryToDelete(null);
    }
  };

  const handleAddToLibrary = async (libraryId) => {
    const selectedSongIds = Object.keys(selectedRows)
      .filter(id => selectedRows[id])
      .map(id => parseInt(id));

    if (selectedSongIds.length === 0) {
      return;
    }

    try {
      // Send single request with array of song IDs
      const result = await dispatch(addBulkLibrarySongs({
        libraryId: parseInt(libraryId),
        songIds: selectedSongIds
      })).unwrap();

      // Show summary from backend response
      setAddSummary({
        added: result.added,
        skipped: result.skipped,
        total: result.total
      });

      // Auto-hide after 5 seconds
      setTimeout(() => setAddSummary(null), 5000);

      // Clear selection after adding
      setSelectedRows({});

      // Refresh library to show updated count
      dispatch(fetchSongLibraries());
    } catch (error) {
      console.error('Error adding songs:', error);
      setAddSummary({
        added: 0,
        skipped: selectedSongIds.length,
        total: selectedSongIds.length
      });
      setTimeout(() => setAddSummary(null), 5000);
    }
  };

  // CSV import handlers
  const handleOpenImport = (library = null) => {
    setImportLibraryId(library ? String(library.id) : (libraries.length === 1 ? String(libraries[0].id) : ''));
    setImportFile(null);
    setImportError(null);
    setImportSummary(null);
    setImportModal(true);
  };

  const closeImportModal = () => {
    setImportModal(false);
    setImportFile(null);
    setImportError(null);
  };

  const handleSubmitImport = async (e) => {
    e.preventDefault();

    if (!importLibraryId || !importFile) {
      setImportError(t('Select a library and a CSV file'));
      return;
    }

    setImportError(null);

    try {
      const result = await dispatch(importSongLibraryFromCsv({
        id: parseInt(importLibraryId),
        file: importFile
      })).unwrap();

      setImportSummary(result);
      setImportModal(false);
      setImportFile(null);

      // Refresh libraries and the global song list (import may create new songs)
      dispatch(fetchSongLibraries());
      dispatch(fetchSongs());
    } catch (error) {
      setImportError(typeof error === 'string' ? t(error) : JSON.stringify(error));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Song edit/delete handlers
  const handleEditSong = (song) => {
    setEditingSong(song);
    setSongTitle(song.title);
    setSongArtist(song.artist);
    setEditSongModal(true);
  };

  const handleDeleteSong = (song) => {
    setSongToDelete(song);
    setDeleteSongModal(true);
  };

  const handleSubmitEditSong = (e) => {
    e.preventDefault();
    if (editingSong) {
      dispatch(updateSong({
        id: editingSong.id,
        data: {
          title: songTitle,
          artist: songArtist
        }
      })).then(() => {
        dispatch(fetchSongs());
        setEditSongModal(false);
        setEditingSong(null);
      });
    }
  };

  const confirmDeleteSong = () => {
    if (songToDelete) {
      dispatch(deleteSong(songToDelete.id)).then(() => {
        dispatch(fetchSongs());
        setDeleteSongModal(false);
        setSongToDelete(null);
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        header: () => {
          const visibleRowIds = table.getRowModel().rows.map(row => row.original.id);
          const allVisibleSelected = visibleRowIds.length > 0 &&
            visibleRowIds.every(id => selectedRows[id]);

          return (
            <input
              type="checkbox"
              className="form-check-input"
              onChange={(e) => handleSelectAll(e.target.checked)}
              checked={allVisibleSelected}
            />
          );
        },
        id: 'select',
        cell: (cell) => (
          <input
            type="checkbox"
            className="form-check-input"
            checked={selectedRows[cell.row.original.id] || false}
            onChange={() => handleRowSelect(cell.row.original.id)}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      ...(isAdmin ? [{
        header: 'ID',
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
      }] : []),
      {
        header: t('Title'),
        accessorKey: "title",
        enableColumnFilter: false,
      },
      {
        header: t('Artist'),
        accessorKey: "artist",
        enableColumnFilter: false,
      },
      ...(isAdmin ? [{
        header: t('Action'),
        id: 'actions',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell) => (
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => handleEditSong(cell.row.original)}
            >
              <i className="ri-pencil-line"></i>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => handleDeleteSong(cell.row.original)}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        ),
      }] : []),
    ],
    [t, songs, selectedRows, isAdmin]
  );

  const table = useReactTable({
    columns,
    data: songs || [],
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  return (
    <React.Fragment>
      <div className="page-content non-admin-layout">
        <Container fluid>
          <BreadCrumb
            title={t('Song Libraries')}
            pageTitle={t('Event View')}
            link={'/admin/event/' + eventId}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <Nav className="nav-tabs-custom rounded card-header-tabs border-bottom-0 nav-success" role="tablist">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => toggleTab('1')}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="fas fa-globe me-1"></i>
                        {t('Global Library')}
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => toggleTab('2')}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="fas fa-music me-1"></i>
                        {t('My Libraries')}
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>
                <CardBody>
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="1">
                      {addSummary && (
                        <Alert color="info" className="mb-3">
                          <div className="d-flex align-items-center">
                            <i className="ri-information-line me-2 fs-4"></i>
                            <div>
                              <strong>{t('Summary')}:</strong>
                              {' '}
                              {addSummary.added > 0 && (
                                <span className="text-success">
                                  {t('Added')}: {addSummary.added}
                                </span>
                              )}
                              {addSummary.added > 0 && addSummary.skipped > 0 && ' | '}
                              {addSummary.skipped > 0 && (
                                <span className="text-warning">
                                  {t('Skipped')} ({t('already in library')}): {addSummary.skipped}
                                </span>
                              )}
                            </div>
                          </div>
                        </Alert>
                      )}

                      {loading && songs.length === 0 ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('Loading...')}</span>
                          </div>
                        </div>
                      ) : error ? (
                        <div className="alert alert-danger" role="alert">
                          <i className="ri-error-warning-line me-2"></i>
                          {t('Error')}: {error}
                        </div>
                      ) : songs.length === 0 ? (
                        <div className="text-center py-4">
                          <div className="mb-3">
                            <i className="ri-music-2-line display-4 text-muted"></i>
                          </div>
                          <h5 className="text-muted">{t('No songs found')}</h5>
                          <p className="text-muted">
                            {t('The song library is empty')}
                          </p>
                        </div>
                      ) : (
                        <>
                          <Row className="mb-3 align-items-center">
                            <Col sm={12} md={4}>
                              <div
                                id="addToLibraryBtnWrapper"
                                onClick={(e) => {
                                  const isDisabled = Object.keys(selectedRows).filter(k => selectedRows[k]).length === 0;
                                  if (isDisabled) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setTooltipOpen(true);
                                    setTimeout(() => setTooltipOpen(false), 2000);
                                  }
                                }}
                                style={{ display: 'inline-block' }}
                              >
                                <Dropdown isOpen={dropdownOpen} toggle={() => {
                                  const isDisabled = Object.keys(selectedRows).filter(k => selectedRows[k]).length === 0;
                                  if (!isDisabled) {
                                    setDropdownOpen(!dropdownOpen);
                                  }
                                }}>
                                  <DropdownToggle
                                    className="btn btn-label btn-success color-primary"
                                    caret
                                    disabled={Object.keys(selectedRows).filter(k => selectedRows[k]).length === 0}
                                  >
                                    <i className="ri-add-line label-icon align-middle fs-16 me-2"></i>
                                    {t('Add to my library')}
                                  </DropdownToggle>
                                  <DropdownMenu>
                                    {libraries.length === 0 ? (
                                      <DropdownItem disabled>
                                        {t('No libraries found')}
                                      </DropdownItem>
                                    ) : (
                                      libraries.map(library => (
                                        <DropdownItem
                                          key={library.id}
                                          onClick={() => handleAddToLibrary(library.id)}
                                        >
                                          {library.name}
                                        </DropdownItem>
                                      ))
                                    )}
                                  </DropdownMenu>
                                </Dropdown>
                              </div>
                              <Tooltip
                                placement="top"
                                isOpen={tooltipOpen}
                                target="addToLibraryBtnWrapper"
                              >
                                {t('First select songs')}
                              </Tooltip>
                            </Col>
                            <Col sm={12} md={4}>
                              <div className="search-box">
                                <Input
                                  type="text"
                                  className="form-control search"
                                  placeholder={t('Search songs...')}
                                  value={globalFilter ?? ''}
                                  onChange={(e) => setGlobalFilter(e.target.value)}
                                />
                                <i className="ri-search-line search-icon"></i>
                              </div>
                            </Col>
                            <Col sm={12} md={4}>
                              <div className="d-flex align-items-center justify-content-md-end">
                                <span className="me-2">{t('Show')}</span>
                                <select
                                  className="form-select"
                                  style={{ width: 'auto' }}
                                  value={table.getState().pagination.pageSize}
                                  onChange={(e) => table.setPageSize(Number(e.target.value))}
                                >
                                  {[10, 25, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                      {pageSize}
                                    </option>
                                  ))}
                                </select>
                                <span className="ms-2">{t('entries')}</span>
                              </div>
                            </Col>
                          </Row>

                          <div className="table-responsive">
                            <Table hover className="table-centered align-middle table-nowrap mb-0">
                              <thead className="text-muted table-light">
                                {table.getHeaderGroups().map((headerGroup) => (
                                  <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                      <th key={header.id}>
                                        {header.isPlaceholder ? null : (
                                          <div
                                            {...{
                                              className: header.column.getCanSort() ? 'cursor-pointer' : '',
                                              onClick: header.column.getToggleSortingHandler(),
                                            }}
                                          >
                                            {flexRender(
                                              header.column.columnDef.header,
                                              header.getContext()
                                            )}
                                          </div>
                                        )}
                                      </th>
                                    ))}
                                  </tr>
                                ))}
                              </thead>
                              <tbody>
                                {table.getRowModel().rows.map((row) => (
                                  <tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => (
                                      <td key={cell.id}>
                                        {flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext()
                                        )}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>

                          <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
                            <div className="col-sm">
                              <div className="text-muted">
                                {t('Total songs')}: <span className="fw-semibold">{songs.length}</span>
                                {' | '}
                                {t('Selected')}: <span className="fw-semibold">{Object.keys(selectedRows).filter(k => selectedRows[k]).length}</span>
                              </div>
                            </div>
                            <div className="col-sm-auto">
                              <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-end mb-0">
                                <li className={!table.getCanPreviousPage() ? "page-item disabled" : "page-item"}>
                                  <Link to="#" className="page-link" onClick={() => table.previousPage()}>
                                    {t('Previous')}
                                  </Link>
                                </li>

                                {table.getState().pagination.pageIndex !== 0 && (
                                  <li className="page-item">
                                    <Link to="#" className="page-link" onClick={() => table.setPageIndex(0)}>
                                      1
                                    </Link>
                                  </li>
                                )}

                                {table.getState().pagination.pageIndex > 1 && (
                                  <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                  </li>
                                )}

                                <li className="page-item active">
                                  <Link to="#" className="page-link">
                                    {table.getState().pagination.pageIndex + 1}
                                  </Link>
                                </li>

                                {table.getState().pagination.pageIndex < table.getPageCount() - 2 && table.getPageCount() > 1 && (
                                  <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                  </li>
                                )}

                                {table.getState().pagination.pageIndex !== table.getPageCount() - 1 && table.getPageCount() > 1 && (
                                  <li className="page-item">
                                    <Link to="#" className="page-link" onClick={() => table.setPageIndex(table.getPageCount() - 1)}>
                                      {table.getPageCount()}
                                    </Link>
                                  </li>
                                )}

                                <li className={!table.getCanNextPage() ? "page-item disabled" : "page-item"}>
                                  <Link to="#" className="page-link" onClick={() => table.nextPage()}>
                                    {t('Next')}
                                  </Link>
                                </li>
                              </ul>
                            </div>
                          </Row>
                        </>
                      )}
                    </TabPane>
                    <TabPane tabId="2">
                      <div className="mb-3 hstack gap-2 flex-wrap">
                        <button type="button" className="btn btn-label btn-success color-primary" onClick={handleAddLibrary}>
                          <i className="ri-add-line label-icon align-middle fs-16 me-2"></i>
                          {t('Add Library')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-label btn-primary"
                          onClick={() => handleOpenImport()}
                          disabled={libraries.length === 0}
                        >
                          <i className="ri-file-upload-line label-icon align-middle fs-16 me-2"></i>
                          {t('Import from CSV')}
                        </button>
                      </div>

                      {importSummary && (
                        <Alert color="info" className="mb-3" toggle={() => setImportSummary(null)}>
                          <div>
                            <strong>{t('Import summary')} ({importSummary.libraryName}):</strong>
                            {' '}
                            <span className="text-success">{t('Added')}: {importSummary.added}</span>
                            {' | '}
                            <span>{t('New songs created')}: {importSummary.created}</span>
                            {' | '}
                            <span className="text-warning">{t('Skipped')} ({t('already in library')}): {importSummary.skipped}</span>
                            {importSummary.invalid > 0 && (
                              <>
                                {' | '}
                                <span className="text-danger">{t('Invalid rows')}: {importSummary.invalid}</span>
                              </>
                            )}
                          </div>
                          {importSummary.invalidRows?.length > 0 && (
                            <div className="mt-2 small text-muted">
                              <div>
                                {t('Examples of invalid rows')}
                                {importSummary.invalidRows.length < importSummary.invalid && (
                                  ' (' + t('showing {{shown}} of {{total}}', {
                                    shown: importSummary.invalidRows.length,
                                    total: importSummary.invalid,
                                  }) + ')'
                                )}:
                              </div>
                              <ul className="mb-0 ps-3">
                                {importSummary.invalidRows.map((row, index) => (
                                  <li key={index}>{row}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {importSummary.limitReached && (
                            <div className="mt-2 small text-danger">
                              {t('Library song limit reached, some songs were not added')}
                            </div>
                          )}
                        </Alert>
                      )}

                      {librariesSuccess && (
                        <Alert color="success" className="mb-3">
                          {t(librariesSuccess)}
                        </Alert>
                      )}

                      {librariesError && (
                        <Alert color="danger" className="mb-3">
                          {typeof librariesError === 'string' ? t(librariesError) : JSON.stringify(librariesError)}
                        </Alert>
                      )}

                      {librariesLoading && libraries.length === 0 ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('Loading...')}</span>
                          </div>
                        </div>
                      ) : libraries.length === 0 ? (
                        <div className="text-center py-4">
                          <div className="mb-3">
                            <i className="ri-folder-music-line display-4 text-muted"></i>
                          </div>
                          <h5 className="text-muted">{t('No libraries found')}</h5>
                          <p className="text-muted">
                            {t('Create your first library by clicking the button above')}
                          </p>
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <Table hover className="table-centered align-middle table-nowrap mb-0">
                            <thead className="text-muted table-light">
                              <tr>
                                <th>{t('Name')}</th>
                                <th>{t('Created At')}</th>
                                <th>{t('Number of Songs')}</th>
                                <th>{t('Action')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {libraries.map((library) => (
                                <tr
                                  key={library.id}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => navigate(`/admin/song-libraries/${library.id}`)}
                                >
                                  <td className="fw-semibold">{library.name}</td>
                                  <td>{formatDate(library.createdAt)}</td>
                                  <td>
                                    <span className="badge bg-success">
                                      {library.songCount || 0}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-info"
                                        title={t('Import from CSV')}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenImport(library);
                                        }}
                                      >
                                        <i className="ri-file-upload-line align-middle"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditLibrary(library);
                                        }}
                                      >
                                        <i className="ri-pencil-line align-middle"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteLibrary(library);
                                        }}
                                      >
                                        <i className="ri-delete-bin-line align-middle"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add/Edit Library Modal */}
      <Modal isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {editingLibrary ? t('Edit Library') : t('Add Library')}
        </ModalHeader>
        <Form onSubmit={handleSubmitLibrary}>
          <ModalBody>
            <FormGroup>
              <Label for="libraryName">{t('Name')}</Label>
              <Input
                id="libraryName"
                type="text"
                value={libraryName}
                onChange={(e) => setLibraryName(e.target.value)}
                placeholder={t('Enter library name')}
                required
                maxLength={60}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button type="button" className="btn btn-label btn-light" onClick={toggleModal}>
                <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
                {t('Close')}
              </button>
              <button type="submit" className="btn btn-label btn-success color-primary">
                <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
                {editingLibrary ? t('Update') : t('Add')}
              </button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Import Songs From CSV Modal */}
      <Modal isOpen={importModal} toggle={closeImportModal}>
        <ModalHeader toggle={closeImportModal}>
          {t('Import from CSV')}
        </ModalHeader>
        <Form onSubmit={handleSubmitImport}>
          <ModalBody>
            {importError && (
              <Alert color="danger">
                {importError}
              </Alert>
            )}
            <FormGroup>
              <Label for="importLibrary">{t('Library')}</Label>
              <Input
                id="importLibrary"
                type="select"
                value={importLibraryId}
                onChange={(e) => setImportLibraryId(e.target.value)}
                required
              >
                <option value="">{t('Select library')}</option>
                {libraries.map(library => (
                  <option key={library.id} value={library.id}>{library.name}</option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="importFile">{t('CSV file')}</Label>
              <Input
                id="importFile"
                type="file"
                accept=".csv,text/csv,.txt"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                required
              />
              <div className="form-text">
                {t('Expected format: artist, title — one song per line.')}
                <br />
                {t('Artist and title can be up to 60 characters each — longer rows are skipped.')}
              </div>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button type="button" className="btn btn-label btn-light" onClick={closeImportModal}>
                <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
                {t('Close')}
              </button>
              <button type="submit" className="btn btn-label btn-success color-primary" disabled={importing}>
                <i className="ri-file-upload-line label-icon align-middle fs-16 me-2"></i>
                {importing ? t('Importing...') : t('Import')}
              </button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Library Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          {t('Confirm Delete')}
        </ModalHeader>
        <ModalBody>
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger"></i>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('Are you sure?')}</h4>
              <p className="text-muted mx-4 mb-0">
                {t('Are you sure you want to delete this library?')}<br/>
                <strong>{libraryToDelete?.name}</strong>
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="hstack gap-2 justify-content-center w-100">
            <button type="button" className="btn btn-label btn-light" onClick={() => setDeleteModal(false)}>
              <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
              {t('Cancel')}
            </button>
            <button type="button" className="btn btn-label btn-danger" onClick={confirmDelete}>
              <i className="ri-delete-bin-line label-icon align-middle fs-16 me-2"></i>
              {t('Delete')}
            </button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Edit Song Modal */}
      <Modal isOpen={editSongModal} toggle={() => setEditSongModal(false)}>
        <ModalHeader toggle={() => setEditSongModal(false)}>
          {t('Edit Song')}
        </ModalHeader>
        <Form onSubmit={handleSubmitEditSong}>
          <ModalBody>
            <FormGroup>
              <Label for="songArtist">{t('Artist')}</Label>
              <Input
                id="songArtist"
                type="text"
                value={songArtist}
                onChange={(e) => setSongArtist(e.target.value)}
                placeholder={t('Enter artist name')}
                required
                maxLength={60}
              />
            </FormGroup>
            <FormGroup>
              <Label for="songTitle">{t('Title')}</Label>
              <Input
                id="songTitle"
                type="text"
                value={songTitle}
                onChange={(e) => setSongTitle(e.target.value)}
                placeholder={t('Enter song title')}
                required
                maxLength={60}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <div className="hstack gap-2 justify-content-end">
              <button type="button" className="btn btn-label btn-light" onClick={() => setEditSongModal(false)}>
                <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
                {t('Close')}
              </button>
              <button type="submit" className="btn btn-label btn-success color-primary">
                <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
                {t('Update')}
              </button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Song Confirmation Modal */}
      <Modal isOpen={deleteSongModal} toggle={() => setDeleteSongModal(false)}>
        <ModalHeader toggle={() => setDeleteSongModal(false)}>
          {t('Confirm Delete')}
        </ModalHeader>
        <ModalBody>
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger"></i>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('Are you sure?')}</h4>
              <p className="text-muted mx-4 mb-0">
                {t('Are you sure you want to delete this song?')}<br/>
                <strong>{songToDelete?.artist} - {songToDelete?.title}</strong>
              </p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="hstack gap-2 justify-content-center w-100">
            <button type="button" className="btn btn-label btn-light" onClick={() => setDeleteSongModal(false)}>
              <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
              {t('Cancel')}
            </button>
            <button type="button" className="btn btn-label btn-danger" onClick={confirmDeleteSong}>
              <i className="ri-delete-bin-line label-icon align-middle fs-16 me-2"></i>
              {t('Delete')}
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SongLibraries;
